import React from "react"
import { useParams, useRouteLoaderData } from "react-router-dom"

import {
  Box,
  Flex,
  Button,
  Alert,
  Text,
  HStack,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  CircularProgress,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
} from "@chakra-ui/react"
import { DataTable } from "./DataTable"
import UploadRecords from "./UploadRecords"
import DeleteConfirmModal from "./DeleteConfirmModal"
import {
  HashedSchemas,
  Schemas,
  UploadProgress,
  FileName,
  IsUploading,
  UploadStatus,
  Limit,
  Offset,
  CurrentFilters,
  SchemaRecordCounts,
  CanDecryptBySchema,
  cacheGetDecrypted,
  cacheSetDecrypted,
} from "../lib/store"
import useFileStore from "../lib/fileStore"

import { Api } from "../lib/api"
import { isEmpty, unrollError } from "../lib/util"
import { FaUpload } from "react-icons/fa6"
import IconLock from "@/icons/IconLock"
import { addAlert, AlertType, removeAlertBySource } from "@/lib/alerts"
import TableFilter from "./TableFilter"
import { MAX_STRING_FILTER_COMBINATIONS } from "@/lib/limits"

const SYSTEM_LIMITS_BANNER_KEY =
  "blindinsight_system_limits_records_banner_dismissed"

export default function SchemaRecordsList() {
  const [limit] = Limit.useState()
  const [offset] = Offset.useState()
  const [isLoading, setIsLoading] = React.useState(false)
  const isLoadingRef = React.useRef(false)
  const user = useRouteLoaderData("auth")
  const { schemaId } = useParams()
  const api = React.useMemo(() => new Api(), [])

  // Initialize state-pool values
  const [schemas] = Schemas.useState()
  const [schemaRecordCounts] = SchemaRecordCounts.useState()
  const [currentFilters] = CurrentFilters.useState()
  const [hashedSchemas] = HashedSchemas.useState()
  const [uploadProgress, setUploadProgress] = UploadProgress.useState()
  const [, setFileName] = FileName.useState()
  const [isUploading, setIsUploading] = IsUploading.useState()
  const clearSelectedFile = useFileStore((state) => state.clearSelectedFile)
  const [deleteRecordId, setDeleteRecordId] = React.useState(null)
  const [deleteRecordsOpen, setDeleteRecordsOpen] = React.useState(false)

  // List of current records in view
  const [records, setRecords] = React.useState([])
  // Total record count from server (X-Total-Count), kept in state so it isn't lost
  const [serverTotalCount, setServerTotalCount] = React.useState(null)
  // Whether there are more pages of filtered results beyond the current page
  const [hasMore, setHasMore] = React.useState(false)
  // Background count fetch state — tracked separately so records render
  // immediately while the total count resolves asynchronously.
  const [isCountLoading, setIsCountLoading] = React.useState(false)
  // Monotonic counter to discard stale count responses after filter changes
  const countRequestRef = React.useRef(0)

  // Find the selected schema
  const schema = React.useMemo(() => schemas[schemaId], [schemas, schemaId])
  // Get the hashed (decrypted) schema
  const hashedSchema = React.useMemo(
    () => hashedSchemas[schemaId],
    [hashedSchemas, schemaId],
  )
  const schemaHasNoRecords = schemaRecordCounts[schemaId] === 0

  // Decryption toggle
  const [encrypted, setEncrypted] = React.useState(true)
  // Can decrypt schema?
  const [canDecryptSchema, setCanDecryptSchema] = React.useState(true)

  // Can decrypt records? Cached at the schema level in state-pool so a
  // remount cascade or route shuffle doesn't re-issue the WASM HMAC
  // round-trip. We hydrate local state from the cache on mount and
  // mirror writes back so the existing render paths keep their
  // primitive-boolean dependency shape.
  const [canDecryptBySchema] = CanDecryptBySchema.useState()
  const cachedCanDecrypt = canDecryptBySchema[schemaId]
  const [canDecryptRecords, setCanDecryptRecords] = React.useState(
    cachedCanDecrypt === true,
  )
  const canDecryptCheckedRef = React.useRef(
    cachedCanDecrypt === undefined ? null : schemaId,
  )

  // Reset canDecryptRecords if the schema changes or decryption becomes unavailable.
  // The actual per-record check fires inline inside updateRecords.
  React.useEffect(() => {
    if (!canDecryptSchema) setCanDecryptRecords(false)
  }, [canDecryptSchema])
  const count = React.useMemo(() => {
    if (serverTotalCount != null) return serverTotalCount
    return -1
  }, [serverTotalCount])

  // Decrypted records keyed by record.id. We hold local state so the table
  // renders instantly when records arrive; the cross-page LRU
  // (cacheGetDecrypted / cacheSetDecrypted) avoids re-decrypting on revisit.
  const [decryptedRecords, setDecryptedRecords] = React.useState({})
  // Schema pending decryption
  const [pendingSchema, setPendingSchema] = React.useState(false)
  // In-flight decrypt requests, kept in a ref so transitions don't trigger
  // re-renders. We previously had a 3-state machine (missing → pending →
  // decrypted) where each transition issued a setState; that produced a
  // render storm and called setMissingRecords from inside a useMemo, which
  // is a render-time side-effect anti-pattern.
  const pendingIdsRef = React.useRef(new Set())
  // Mirror of decryptedRecords for use inside the decrypt effect without
  // adding it to the dep array (would re-fire on every successful decrypt
  // and double-issue requests for any straggler).
  const decryptedRecordsRef = React.useRef(decryptedRecords)
  React.useEffect(() => {
    decryptedRecordsRef.current = decryptedRecords
  }, [decryptedRecords])

  const properties = React.useMemo(
    () => schema?.schema?.properties || {},
    [schema],
  )

  // Decrypt the schema eagerly — fires on mount in parallel with the records fetch.
  // Schema decryption doesn't need records, so we don't wait for them.
  React.useEffect(() => {
    if (!schema) return
    if (!canDecryptSchema) return
    if (pendingSchema) return
    if (hashedSchemas[schema.id]) return
    setPendingSchema(true)
    api
      .schemasDecrypt(schema)
      .then(() => setPendingSchema(false))
      .catch(() => {
        setPendingSchema(false)
        setCanDecryptSchema(false)
      })
  }, [
    hashedSchemas,
    schema,
    pendingSchema,
    setPendingSchema,
    canDecryptSchema,
    setCanDecryptSchema,
  ])

  const PROXY_CACHE_KEY = "BI_HAS_PROXY"
  const [hasProxy, setHasProxy] = React.useState(
    () => sessionStorage.getItem(PROXY_CACHE_KEY) === "true",
  )

  React.useEffect(() => {
    // Trust the sessionStorage cache once set: behind-proxy state is
    // stable for the lifetime of a tab, and `statusRetrieve` is itself
    // proxied through the WASM extension path on customer setups, which
    // adds a noticeable round-trip per page nav. Only fire on first
    // mount of the session, not on every remount.
    if (sessionStorage.getItem(PROXY_CACHE_KEY) !== null) return
    api.statusRetrieve().then((resp) => {
      const behind = Boolean(resp?.behind_proxy)
      sessionStorage.setItem(PROXY_CACHE_KEY, behind)
      setHasProxy(behind)
    })
  }, [])

  // Decrypt records as they arrive. One effect, one transition per batch.
  // Cache hits populate decryptedRecords without an API call; cache misses
  // are batched into a single /records/decrypt/ POST. Reads decryptedRecords
  // through a ref so a successful decrypt doesn't re-fire the effect.
  React.useEffect(() => {
    if (encrypted) return
    if (!canDecryptRecords) return
    if (!records.length) return

    const current = decryptedRecordsRef.current
    const fromCache = {}
    const missing = []
    for (const rec of records) {
      if (current[rec.id]) continue
      if (pendingIdsRef.current.has(rec.id)) continue
      const cached = cacheGetDecrypted(rec.id)
      if (cached !== undefined) {
        fromCache[rec.id] = cached
      } else {
        missing.push(rec)
      }
    }

    if (Object.keys(fromCache).length > 0) {
      setDecryptedRecords((prev) => ({ ...prev, ...fromCache }))
    }

    if (missing.length === 0) return

    for (const rec of missing) pendingIdsRef.current.add(rec.id)
    let cancelled = false
    const decryptStart = performance.now()
    api
      .recordsDecrypt(missing)
      .then((data) => {
        if (cancelled) return
        const newEntries = {}
        for (const record of data) {
          newEntries[record.id] = record.data
        }
        cacheSetDecrypted(newEntries)
        setDecryptedRecords((prev) => ({ ...prev, ...newEntries }))
        const ms = Math.round(performance.now() - decryptStart)
        console.log(
          `%c⚡ [Blind Insight] Decrypt batch (${missing.length} records): ${ms}ms`,
          "color: #6366f1; font-weight: 600",
        )
      })
      .catch((error) => {
        console.error("Error decrypting records", error)
        if (error?.status === 404) {
          addAlert(
            AlertType.ERROR,
            "Decrypted records not found",
            "Decrypted records not found, please refresh the page.",
          )
          return
        }
        setEncrypted(true)
        if ([403, 422].includes(error?.status)) {
          addAlert(
            AlertType.ERROR,
            "Permission Denied",
            "You do not have permission to decrypt these records.",
            10,
          )
          setCanDecryptRecords(false)
          return
        }
        // TODO(shakefu): This 405 is a wonky status code.
        if (error?.status === 405) {
          console.error("Decrypt not available on API")
        }
      })
      .finally(() => {
        for (const rec of missing) pendingIdsRef.current.delete(rec.id)
      })

    return () => {
      cancelled = true
    }
  }, [encrypted, canDecryptRecords, records, api])

  // Render nothing if we don't have a user
  if (!user) return null
  // Render a not found if we couldn't find a schema in state
  if (!schema) {
    addAlert(AlertType.ERROR, "Schema Not Found", "Schema not found")
  }

  const toggleEncryption = React.useCallback(() => {
    if (!canDecryptRecords && encrypted) return
    setEncrypted(!encrypted)
  }, [encrypted, canDecryptRecords, setEncrypted])

  const fieldNames = React.useMemo(() => {
    // Defer until the schema decrypts so column labels don't flicker
    // between the encrypted-keys fallback and the resolved field names.
    if (pendingSchema) return []

    if (hashedSchema && !isEmpty(hashedSchema)) {
      return Object.keys(hashedSchema).map((key) =>
        encrypted ? key : hashedSchema[key]?.name,
      )
    }
    // Fallback: derive from the encrypted record blobs themselves. Only
    // hit when the schema definition isn't yet available — rare in
    // practice because schema fetch precedes records fetch.
    const keys = new Set()
    for (const record of records) {
      if (!record?.data) continue
      for (const k of Object.keys(record.data)) keys.add(k)
    }
    return Array.from(keys)
  }, [hashedSchema, encrypted, records, pendingSchema])

  // missing here is the idField in the row - JP just can't
  const columns = React.useMemo(() => {
    return fieldNames.map((name) => ({
      accessorKey: name,
      header: name,
      field: name,
      type: properties[name]?.type,
    }))
  }, [fieldNames, properties])

  const data = React.useMemo(() => {
    return records.map((record) => {
      if (encrypted) {
        return { id: record.id, ...record.data }
      }
      return { id: record.id, ...decryptedRecords[record.id] }
    })
  }, [records, decryptedRecords, encrypted])

  function checkForFilteredRecords(schemaId, count) {
    const recordCounts = SchemaRecordCounts.getValue()
    SchemaRecordCounts.setValue({
      ...recordCounts,
      [schemaId]: count,
    })
  }

  // Fire once per schema when the first batch of records arrives.
  // Runs inline inside updateRecords to avoid waiting for a render cycle.
  const checkCanDecrypt = React.useCallback(
    (list) => {
      if (!list?.length) return
      if (canDecryptCheckedRef.current === schemaId) return
      // Cross-mount cache: if another instance already resolved this for
      // this schema, hydrate from state-pool and skip the API call. The
      // recordsCanDecrypt round-trip is expensive (WASM HMAC + chrome
      // .storage round-trips) so this is the dominant Wave 2 saving.
      const cached = CanDecryptBySchema.getValue()[schemaId]
      if (cached !== undefined) {
        canDecryptCheckedRef.current = schemaId
        setCanDecryptRecords(cached)
        return
      }
      canDecryptCheckedRef.current = schemaId
      api
        .recordsCanDecrypt(list[0])
        .then((resp) => {
          const value = Boolean(resp?.can_decrypt)
          setCanDecryptRecords(value)
          CanDecryptBySchema.updateValue((m) => {
            m[schemaId] = value
          })
        })
        .catch((error) => {
          console.error("Error checking if record can be decrypted", error)
          setCanDecryptRecords(false)
          // Persist the failure so a remount storm doesn't retry on
          // every mount in a doom loop. A real recovery path (login,
          // permission grant) clears this on logout.
          CanDecryptBySchema.updateValue((m) => {
            m[schemaId] = false
          })
          canDecryptCheckedRef.current = null
        })
    },
    [schemaId, api],
  )

  // Prefetch staging — holds the next-page response so a Next-button click
  // can skip the search round-trip. Cleared on filter/schema change.
  const prefetchRef = React.useRef({
    offset: null,
    resp: null,
    controller: null,
  })

  const cancelPrefetch = React.useCallback(() => {
    if (prefetchRef.current.controller) {
      prefetchRef.current.controller.abort()
    }
    prefetchRef.current = { offset: null, resp: null, controller: null }
  }, [])

  const schedulePrefetch = React.useCallback(
    (nextOffset) => {
      // Only one prefetch in flight at a time.
      cancelPrefetch()
      const controller = new AbortController()
      prefetchRef.current = { offset: nextOffset, resp: null, controller }

      const run = () => {
        if (controller.signal.aborted) return
        api
          .recordsSearch(
            schemaId,
            currentFilters,
            limit,
            nextOffset,
            controller.signal,
          )
          .then((resp) => {
            if (controller.signal.aborted) return
            if (prefetchRef.current.controller === controller) {
              prefetchRef.current = {
                offset: nextOffset,
                resp,
                controller: null,
              }
            }
            // Best-effort: warm the LRU decrypted cache so when the user
            // actually clicks Next, the decrypt effect short-circuits to
            // a cache hit and no decrypt POST is issued. Errors silent.
            if (!encrypted && canDecryptRecords) {
              const list = Array.isArray(resp) ? resp : resp?.results ?? []
              const toDecrypt = list.filter(
                (r) => cacheGetDecrypted(r.id) === undefined,
              )
              if (toDecrypt.length > 0) {
                api
                  .recordsDecrypt(toDecrypt)
                  .then((data) => {
                    if (controller.signal.aborted) return
                    const entries = {}
                    for (const record of data) {
                      entries[record.id] = record.data
                    }
                    cacheSetDecrypted(entries)
                  })
                  .catch(() => {})
              }
            }
          })
          .catch(() => {
            // Prefetch failures are silent; the user-driven fetch retries.
          })
      }

      if (
        typeof window !== "undefined" &&
        typeof window.requestIdleCallback === "function"
      ) {
        window.requestIdleCallback(run, { timeout: 1000 })
      } else {
        setTimeout(run, 0)
      }
    },
    [
      api,
      schemaId,
      currentFilters,
      limit,
      encrypted,
      canDecryptRecords,
      cancelPrefetch,
    ],
  )

  const updateRecords = React.useCallback(
    async (signal) => {
      isLoadingRef.current = true
      setIsLoading(true)
      setServerTotalCount(null)
      const t0 = performance.now()
      let prefetchHit = false
      try {
        let resp
        if (currentFilters.length > 0) {
          // Prefetch hit: consume the staged response for this offset
          // and skip the network round-trip entirely.
          if (
            prefetchRef.current.offset === offset &&
            prefetchRef.current.resp
          ) {
            resp = prefetchRef.current.resp
            prefetchHit = true
            prefetchRef.current = { offset: null, resp: null, controller: null }
          } else {
            resp = await api.recordsSearch(
              schemaId,
              currentFilters,
              limit,
              offset,
              signal,
            )
          }
          if (signal?.aborted) return
          const list = Array.isArray(resp) ? resp : resp?.results ?? []
          setHasMore(list.length >= limit)
          setRecords(list)
          checkCanDecrypt(list)
          if (typeof resp?.count === "number" && !Number.isNaN(resp.count)) {
            setServerTotalCount(resp.count)
          } else if (resp?.xQuery) {
            // Records arrived without a total count — fire a lightweight
            // count-only request in the background using the server's X-Query
            // header so the pagination total resolves without blocking the UI.
            const requestId = ++countRequestRef.current
            setIsCountLoading(true)
            api
              .recordsCount(resp.xQuery)
              .then((countResp) => {
                if (requestId !== countRequestRef.current) return
                const n = countResp?.count
                if (typeof n === "number" && !Number.isNaN(n)) {
                  setServerTotalCount(n)
                }
              })
              .catch((err) => {
                console.warn("Background count request failed", err)
              })
              .finally(() => {
                if (requestId === countRequestRef.current) {
                  setIsCountLoading(false)
                }
              })
          }

          // Kick off prefetch of the next page in the background. We only
          // stage the ciphertext + warm the LRU decrypted cache; the user's
          // Next click then short-circuits the search round-trip and the
          // decrypt batch in one motion.
          if (list.length >= limit) {
            schedulePrefetch(offset + limit)
          }
        } else {
          resp = await api.recordsList(schemaId, limit, offset, signal)
          if (signal?.aborted) return
          setHasMore(false)
          const list = Array.isArray(resp) ? resp : resp?.results ?? []
          setRecords(list)
          checkCanDecrypt(list)
          setServerTotalCount(
            typeof resp?.count === "number" && !Number.isNaN(resp.count)
              ? resp.count
              : null,
          )
          if (SchemaRecordCounts.getValue()[schemaId] === undefined) {
            checkForFilteredRecords(schemaId, resp?.count)
          }
        }
      } catch (error) {
        if (error?.name === "AbortError" || signal?.aborted) return
        if (error.status === 413) {
          const errorMessage = await unrollError(error)
          addAlert(
            AlertType.ERROR,
            "System Limit Exceeded",
            errorMessage ||
              "Query exceeds system limits. Please use filters to narrow your query or reduce the number of text filter combinations.",
          )
        } else {
          addAlert(
            AlertType.ERROR,
            "Error fetching records",
            await unrollError(error),
          )
        }
      } finally {
        if (!signal?.aborted) {
          const ms = Math.round(performance.now() - t0)
          console.log(
            `%c⚡ [Blind Insight] Query roundtrip: ${ms}ms${
              prefetchHit ? " (prefetch hit)" : ""
            }`,
            "color: #6366f1; font-weight: 600",
          )
          isLoadingRef.current = false
          setIsLoading(false)
        }
      }
    },
    [schemaId, currentFilters, limit, offset, api, schedulePrefetch],
  )

  React.useEffect(() => {
    const controller = new AbortController()
    removeAlertBySource("Error fetching records")
    removeAlertBySource("Decrypted records not found")
    removeAlertBySource("Permission Denied")
    updateRecords(controller.signal)
    return () => {
      controller.abort()
      // Stop any background prefetch so we don't pollute the LRU cache or
      // surface a stale ciphertext page after filters/schema changed.
      cancelPrefetch()
    }
  }, [currentFilters, limit, offset, schemaId])

  // Remove the pagination dependency from the other effect
  React.useEffect(() => {
    if (isUploading === UploadStatus.COMPLETED) {
      updateRecords()
    }
  }, [isUploading])

  const [isOpen, setIsOpen] = React.useState(false)

  const clearStates = () => {
    if (isUploading === UploadStatus.COMPLETED) {
      updateRecords()
      setUploadProgress(0)
      setIsUploading(UploadStatus.NONE)
      setFileName(null)
    }
  }

  const openModal = () => {
    clearStates()
    setIsOpen(true)
  }
  const closeModal = () => {
    clearStates()
    removeAlertBySource("File Upload")
    clearSelectedFile()
    setIsOpen(false)
  }

  // TODO(jathan): Since this is a component, is it wise to use a callback?
  const EncryptButton = React.useCallback(() => {
    return (
      <>
        <IconLock
          locked="Danger.100"
          unlocked="Success.100"
          encrypted={encrypted.toString()}
        />
        <Button
          variant="secondary"
          size="xs"
          onClick={toggleEncryption}
          isDisabled={canDecryptRecords === false}
          title={
            canDecryptRecords
              ? "Toggle encryption"
              : "You must be an authorized data owner and decrypt locally using the proxy."
          }
        >
          {encrypted ? "Decrypt" : "Encrypt"}
        </Button>
      </>
    )
  }, [encrypted, canDecryptRecords, toggleEncryption])

  const [showBanner, setShowBanner] = React.useState(false)

  React.useEffect(() => {
    const dismissed = localStorage.getItem(SYSTEM_LIMITS_BANNER_KEY)
    if (!dismissed) {
      setShowBanner(true)
    }
  }, [])

  const handleDismissBanner = () => {
    localStorage.setItem(SYSTEM_LIMITS_BANNER_KEY, "true")
    setShowBanner(false)
  }

  return (
    <Flex direction="column" flexGrow={1} minHeight={0}>
      <HStack justifyContent="space-between" alignItems="flex-start">
        <Text>{schema.description}</Text>
        <HStack spacing={2}>
          <Button
            flexShrink={0}
            variant={schemaHasNoRecords ? "primary" : "secondary"}
            title="Upload Data to Blind Insight"
            size="xs"
            h={6}
            minH={6}
            px={2.5}
            fontSize="xs"
            rightIcon={
              isUploading === UploadStatus.UPLOADING ? (
                <CircularProgress size={"1.5em"} value={uploadProgress} />
              ) : (
                <FaUpload />
              )
            }
            onClick={openModal}
          >
            {isUploading === UploadStatus.UPLOADING ? "Uploading" : "Upload"}
          </Button>
        </HStack>
      </HStack>
      {showBanner && (
        <Alert
          status="info"
          borderRadius="md"
          position="relative"
          mt={4}
          mb={4}
        >
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>System Limits</AlertTitle>
            <AlertDescription>
              <Text fontSize="sm">
                <strong>Query Performance:</strong> Using more than{" "}
                {MAX_STRING_FILTER_COMBINATIONS} text filter combinations in a
                single query may result in slower performance. Consider reducing
                the number of text filters or using more specific values.
              </Text>
            </AlertDescription>
          </Box>
          <CloseButton
            alignSelf="flex-start"
            position="relative"
            right={-1}
            top={-1}
            onClick={handleDismissBanner}
          />
        </Alert>
      )}
      <Box>
        {hasProxy === false && (
          <Alert width="fit-content" status="info" variant="standard">
            <Text>
              Please{" "}
              <Link
                href="https://docs.blindinsight.io/download/"
                isExternal
                rel="noreferrer"
              >
                download and run the Blind Proxy
              </Link>{" "}
              to search and decrypt locally. Feel free to get in touch if you
              need assistance.
            </Text>
          </Alert>
        )}
        {hasProxy && <TableFilter />}
      </Box>
      <Flex direction="column" flexGrow={1} minHeight={0}>
        <DataTable
          schemaId={schemaId}
          EncryptButton={EncryptButton}
          hasProxy={hasProxy}
          data={data}
          columns={columns}
          rowCount={count}
          hasMore={hasMore}
          onUpdateRecords={updateRecords}
          isLoading={isLoading}
          isCountLoading={isCountLoading}
          onDeleteRow={(recordId) => {
            setDeleteRecordId(recordId)
            setDeleteRecordsOpen(true)
          }}
        />
      </Flex>
      <DeleteConfirmModal
        isOpen={deleteRecordsOpen}
        onClose={() => {
          setDeleteRecordsOpen(false)
          setDeleteRecordId(null)
          updateRecords()
        }}
        type="records"
        selectedIds={deleteRecordId ? [deleteRecordId] : []}
      />
      <Modal isOpen={isOpen} onClose={closeModal} variant="dataUpload">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <UploadRecords />
        </ModalContent>
      </Modal>
    </Flex>
  )
}
